import { Subject } from 'rxjs';
import { User } from "oidc-client-ts";
import { getUserFromLocalStorage } from './oidc';
import moment from 'moment';

const REFRESH_TOKEN_TIME_IN_SECONDS = 15;
const TOKEN_EXPIRATION_TOLERANCE_IN_SECONDS = 30;

export default class IframeService {
  private static instance: IframeService;

  private loopProtection = 0;
  private values = new Map<string, any>();
  private messageSubject = new Subject<any>();
  private iframe: HTMLIFrameElement | undefined;
  private relativePath: string | undefined;
  private user: User | undefined;
  private debugMode = window.plasma.environment.DEBUG === true || window.plasma.environment.DEBUG === 'true';
  private allowedOrigins: string[] = typeof window.plasma.environment.IFRAME_ORIGINS === 'string'
    ? window.plasma.environment.IFRAME_ORIGINS.split(',').map(origin => origin.trim())
    : [];

  private constructor() {
    window.addEventListener('message', (event) => {
      // Check if the message is from the expected origin
      if (this.allowedOrigins.findIndex((v) => v === '*' || v === event.origin) == -1) {
        console.warn('ViridisNG - Mensagem ignorada de origem desconhecida:', event.origin);
        return;
      }

      this.debugMode && console.log('ViridisNG - Mensagem recebida:', {
        origin: event.origin,
        data: event.data,
        timestamp: new Date().toISOString(),
        source: event.source
      });

      if (event.data?.type) {
        this.values.set(event.data.type, event.data.data);
      }
      this.messageSubject.next(event.data);
    });

    this.messageSubject.subscribe({
      next: (message) => {
        // Check if the message is of the expected type
        if (message.type === 'auth.expiresAt') {
          this.navigate(message.data);
        }
      }
    });

    setInterval(() => this.refreshToken(), 1000 * REFRESH_TOKEN_TIME_IN_SECONDS);
  }

  private static getInstance() {
    return (this.instance != null) ? this.instance : (this.instance = new IframeService());
  }

  private hasType(type: string) {
    return this.values.has(type);
  }

  private getValue(type: string) {
    return this.values.get(type);
  }

  private sendMessage(message: any) {
    if (this.iframe?.contentWindow) {
      this.debugMode && console.log('ViridisNG - Enviando mensagem:', {
        type: message.type,
        data: message.data,
        timestamp: new Date().toISOString()
      });
      this.iframe.contentWindow.postMessage(message, '*');
    } else {
      console.warn('Mensagem ignorada, iframe inacessível:', message);
    }
  }

  private navigate(value: string) {
    const expiresAt = this.user?.expires_at ? moment.unix(this.user.expires_at).toISOString() : undefined;
    const isValueOrExpiresAtMissing = value == null || expiresAt == null;
    const diff_seconds = isValueOrExpiresAtMissing
      ? TOKEN_EXPIRATION_TOLERANCE_IN_SECONDS + 1
      : Math.abs(moment(value).diff(moment(expiresAt), 'seconds'));

    if (this.debugMode) {
      console.log('ViridisNG - Navegando com expiresAt:', expiresAt, 'e value:', value, 'diff_seconds:', diff_seconds);
    }

    if (this.loopProtection <= 0 && diff_seconds > TOKEN_EXPIRATION_TOLERANCE_IN_SECONDS) {
      this.loopProtection = this.loopProtection + 1;
      setTimeout(() => this.loopProtection = this.loopProtection - 1, 500);
      this.sendMessage({ type: 'auth.security', data: this.user });
    } else if (diff_seconds < TOKEN_EXPIRATION_TOLERANCE_IN_SECONDS) {
      this.sendMessage({ type: 'router.relativePath', data: this.relativePath });
    }
  }

  private refreshToken = () => {
    const user = getUserFromLocalStorage();
    if (user && user.access_token !== this.user?.access_token) {
      this.user = user;
      this.sendMessage({ type: 'auth.refreshToken', data: { ...this.user, refreshToken: true } });
    }
  }

  private reloadIframe() {
    this.sendMessage({ type: 'RELOAD' });
  }

  public static navigate(iframe: HTMLIFrameElement | undefined, relativePath: string, user: User | undefined) {
    const iframeService = IframeService.getInstance();
    iframeService.iframe = iframe;
    iframeService.relativePath = relativePath;
    iframeService.user = user;
    if (iframeService.hasType('auth.expiresAt')) {
      iframeService.navigate(iframeService.getValue('auth.expiresAt'));
    }
  }

  public static reloadIframe() {
    const iframeService = IframeService.getInstance();
    iframeService.reloadIframe();
  }
}
